/**
 * The plans a visitor may choose at signup, and the arithmetic behind the summary line.
 *
 * Read live from `subscriptionPlans` — never a hard-coded list, because Ahmed edits plans in
 * Settings and a plan he deactivates must disappear from signup without a release.
 *
 * PUBLIC means active and not hidden: `isPublic === false` is the academy's admin-only marker
 * (the Sponsored plan carries it). A plan with no `isPublic` field predates the flag and counts
 * as public, which is how every ordinary plan is stored.
 *
 * `classLimit` is the class count for the WHOLE period, not per month: 3Mo 8Cls holds 24. The
 * member thinks in classes per month, so that is what the option says.
 */

export interface PlanLike {
    id: string;
    name: string;
    /** Arabic name, when the academy has set one in Settings. */
    nameAr?: string;
    price: number;
    durationDays?: number;
    duration?: number;
    classLimit?: number | null;
    planType?: string;
    isActive?: boolean;
    isPublic?: boolean;
}

export interface PublicPlan {
    id: string;
    name: string;
    nameAr?: string;
    price: number;
    durationDays: number;
    /** Classes per month, or null for unlimited. */
    classesPerMonth: number | null;
    months: number;
}

export type PlanGroup = 'monthly' | 'months_3' | 'months_6' | 'months_12' | 'other';

const daysOf = (p: PlanLike): number => {
    const d = typeof p.durationDays === 'number' && p.durationDays > 0 ? p.durationDays
        : typeof p.duration === 'number' && p.duration > 0 ? p.duration : 30;
    return d;
};

/** Active, and not marked admin-only. */
export const isPublicPlan = (p: PlanLike): boolean => p.isActive !== false && p.isPublic !== false;

export const monthsOf = (p: PlanLike): number => Math.max(1, Math.round(daysOf(p) / 30));

export function toPublicPlan(p: PlanLike): PublicPlan {
    const months = monthsOf(p);
    const limit = p.classLimit;
    return {
        id: p.id, name: p.name, nameAr: p.nameAr, price: Number(p.price) || 0, durationDays: daysOf(p), months,
        classesPerMonth: typeof limit === 'number' && limit > 0 ? Math.round(limit / months) : null,
    };
}

/** The visitor's plans: public ones only, monthly first, then 3, 6, 12 months; cheapest first. */
export function publicPlans(plans: readonly PlanLike[]): PublicPlan[] {
    return plans.filter(isPublicPlan).map(toPublicPlan)
        .sort((a, b) => a.durationDays - b.durationDays || a.price - b.price);
}

export const groupOf = (p: PublicPlan): PlanGroup =>
    p.months === 1 ? 'monthly' : p.months === 3 ? 'months_3' : p.months === 6 ? 'months_6' : p.months === 12 ? 'months_12' : 'other';

/** Plans in the order they are offered, grouped by how long they run. */
export function groupPlans(plans: readonly PublicPlan[]): { group: PlanGroup; plans: PublicPlan[] }[] {
    const order: PlanGroup[] = ['monthly', 'months_3', 'months_6', 'months_12', 'other'];
    return order
        .map(group => ({ group, plans: plans.filter(p => groupOf(p) === group) }))
        .filter(g => g.plans.length > 0);
}

/** What this plan works out to per month. */
export const pricePerMonth = (p: PublicPlan): number => Math.round(p.price / p.months);

/**
 * What a longer plan saves against paying monthly for the same thing — the reason anyone picks
 * one. Compared with the monthly plan offering the same classes per month (8, 12, unlimited);
 * returns 0 when there is no comparable monthly plan or the longer plan is not cheaper.
 */
export function savingVsMonthly(p: PublicPlan, all: readonly PublicPlan[]): number {
    if (p.months <= 1) return 0;
    const monthly = all.find(m => m.months === 1 && m.classesPerMonth === p.classesPerMonth);
    if (!monthly) return 0;
    return Math.max(0, monthly.price * p.months - p.price);
}

/**
 * Does this read like one of our internal codes rather than a name for a member?
 * `3Mo 8Cls`, `12Mo UNL`, `6Mo 4Cls` — the shape the academy has always used in Settings.
 */
export const looksLikeCode = (name: string | undefined | null): boolean => {
    const n = String(name ?? '').trim();
    if (!n) return true;
    return /\d+\s*Mo\b/i.test(n) || /\bUNL\b/i.test(n) || /\bCls\b/i.test(n);
};

/**
 * The plan's name in the reader's language.
 *
 * A plan whose name is missing or is an internal code gets a label built from its own data —
 * "3 Months · 8 classes" — so a plan someone adds later called `6Mo 4Cls` still reads properly
 * to a member, with no release and nothing to remember (Ahmed, 26 Sept 2026).
 */
export function planName(p: Pick<PublicPlan, 'name' | 'nameAr' | 'months' | 'classesPerMonth'>, locale: string): string {
    const ar = locale === 'ar';
    const given = (ar && p.nameAr?.trim()) ? p.nameAr.trim() : p.name;
    if (!looksLikeCode(given)) return given;
    if (ar && p.nameAr?.trim() && !looksLikeCode(p.nameAr)) return p.nameAr.trim();
    return describePlan(p, locale);
}

/** "3 Months · 8 classes" / "٣ شهور، ٨ حصص" — built from the plan's own duration and limit. */
export function describePlan(p: Pick<PublicPlan, 'months' | 'classesPerMonth'>, locale: string): string {
    const ar = locale === 'ar';
    const n = (x: number) => formatCount(x, locale);
    const duration = p.months <= 1
        ? (ar ? 'شهري' : 'Monthly')
        : (ar ? `${n(p.months)} ${p.months <= 10 ? 'شهور' : 'شهر'}` : `${n(p.months)} Months`);
    const classes = p.classesPerMonth === null
        ? (ar ? 'غير محدود' : 'Unlimited')
        : (ar ? `${n(p.classesPerMonth)} ${p.classesPerMonth <= 10 ? 'حصص' : 'حصة'}` : `${n(p.classesPerMonth)} classes`);
    return ar ? `${duration}، ${classes}` : `${duration} · ${classes}`;
}

/** A count in the reader's own digits — "8" beside "٩٠٠ ج.م" reads as two languages at once. */
export const formatCount = (n: number, locale: string): string =>
    new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-EG').format(n);

/** "EGP 900" / "٩٠٠ ج.م" — the money, in the reader's language. */
export function formatEgp(amount: number, locale: string): string {
    const n = new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-EG').format(Math.round(amount));
    return locale === 'ar' ? `${n} ج.م` : `EGP ${n}`;
}

/** The value stored when someone would rather decide at the academy. */
export const UNDECIDED_PLAN = 'undecided';

/** What is written on the new account — a request, never a subscription. */
export interface RequestedPlan {
    requestedPlanId: string;
    requestedPlanName: string;
    requestedPlanPrice: number;
    requestedPlanDurationDays: number | null;
    requestedPlanAt: Date;
}

export function requestedPlanOf(plan: PublicPlan | null, now = new Date()): RequestedPlan {
    return plan
        ? {
            requestedPlanId: plan.id, requestedPlanName: plan.name, requestedPlanPrice: plan.price,
            requestedPlanDurationDays: plan.durationDays, requestedPlanAt: now,
        }
        : {
            requestedPlanId: UNDECIDED_PLAN, requestedPlanName: UNDECIDED_PLAN, requestedPlanPrice: 0,
            requestedPlanDurationDays: null, requestedPlanAt: now,
        };
}

/**
 * The plan name to show a member for a SUBSCRIPTION they already hold.
 *
 * `planName` is copied onto the subscription when it is created, so a plan renamed later leaves
 * old rows carrying the old text — 62 active rows still held codes like "3Mo 8Cls" on 26 Sept
 * 2026. Prefer the plan's current name; fall back to the stored one; and if that is a code, or
 * there is nothing at all, describe the plan from whatever the row itself carries.
 */
export function subscriptionPlanName(
    sub: { planId?: string; planName?: string; classLimit?: number | null; durationDays?: number },
    plans: readonly PublicPlan[],
    locale: string,
): string {
    const current = sub.planId ? plans.find(p => p.id === sub.planId) : undefined;
    if (current) return planName(current, locale);
    if (sub.planName && !looksLikeCode(sub.planName)) return sub.planName;
    const months = Math.max(1, Math.round((sub.durationDays ?? 30) / 30));
    const limit = sub.classLimit;
    return describePlan({ months, classesPerMonth: typeof limit === 'number' && limit > 0 ? Math.round(limit / months) : null }, locale);
}
